// /bots: the Discord bots by Penrose. Two cards (Penrose community bot, Cadence
// music bot) with a short pitch and a link to each bot's site.
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

type BotEntry = {
  name: string;
  tagline: string;
  description: string;
  features: string[];
  href: string;
  external: boolean;
  logo: string;
};

const BOTS: BotEntry[] = [
  {
    name: 'Penrose',
    tagline: 'All-in-one community bot',
    description:
      'The backbone for running a Discord server: moderation, anti-nuke, and automod, plus leveling, tickets, modmail, welcome flows, reaction roles, Join-to-Create voice, an AI assistant, and a full web dashboard.',
    features: ['Moderation & anti-nuke', 'Leveling & economy', 'Tickets & modmail', 'AI assistant', 'Web dashboard'],
    href: '/penrose',
    external: false,
    logo: '/assets/penrose-logo.png',
  },
  {
    name: 'Cadence',
    tagline: 'High-quality music bot',
    description:
      'Crystal-clear music from every major source with a modern web player, live synced lyrics, hundreds of radio stations, and a collaborative room player anyone in the call can control from their phone.',
    features: ['Multi-source playback', 'Web player', 'Live synced lyrics', '200+ radio stations', 'Room player'],
    href: 'https://cadence.streamnook.app',
    external: true,
    logo: '/assets/cadence-logo.png',
  },
];

export default function Bots() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Discord Bots</h1>
        <p className="text-textSecondary mt-1 max-w-2xl">
          Two bots by Penrose to power your Discord server — a full-featured community bot and a
          high-quality music bot. Add one or both.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {BOTS.map((b) => {
          const cta = (
            <>
              Visit site
              <ExternalLink className="h-4 w-4" />
            </>
          );
          return (
            <article key={b.name} className="settings-card flex flex-col gap-5 p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[var(--color-borderLight)] bg-[var(--color-surface)] p-2">
                  <img src={b.logo} alt={`${b.name} logo`} className="h-full w-full object-contain" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold text-textPrimary">{b.name}</h2>
                  <p className="text-sm text-accent">{b.tagline}</p>
                </div>
              </div>

              <p className="flex-1 text-sm leading-relaxed text-textSecondary">{b.description}</p>

              <ul className="flex flex-wrap gap-1.5">
                {b.features.map((f) => (
                  <li
                    key={f}
                    className="rounded-full border border-[var(--color-borderLight)] bg-[var(--color-surface)] px-2.5 py-1 text-[11px] text-textSecondary"
                  >
                    {f}
                  </li>
                ))}
              </ul>

              {b.external ? (
                <a
                  href={b.href}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2 self-start px-4 py-2 text-sm"
                >
                  {cta}
                </a>
              ) : (
                <Link
                  to={b.href}
                  className="btn-primary inline-flex items-center justify-center gap-2 self-start px-4 py-2 text-sm"
                >
                  {cta}
                </Link>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
